// Settings Helper Functions - Utility operations for settings management
// Improved with proper generic typing and clear purpose

import { SettingValue } from '@edgerunner/shared/src/types/settingsTypes';

const FORBIDDEN_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

// Helper to set a value in a nested object based on a path string
// Creates intermediate objects as needed for paths like "launch_options.autoStart"
export function setNestedValue<T extends Record<string, any>>(
    obj: T,
    path: string,
    value: SettingValue
): T {
    const keys = path.split('.');
    let current: any = obj;
    for (let i = 0; i < keys.length - 1; i += 1) {
        const key = keys[i];
        if (FORBIDDEN_KEYS.has(key)) throw new Error(`Rejected forbidden key in settings path: ${key}`);
        current[key] = current[key] || {};
        current = current[key]; // nosemgrep: javascript.lang.security.audit.prototype-pollution.prototype-pollution-loop.prototype-pollution-loop
    }
    const lastKey = keys[keys.length - 1];
    if (FORBIDDEN_KEYS.has(lastKey)) throw new Error(`Rejected forbidden key in settings path: ${lastKey}`);
    current[lastKey] = value;
    return obj;
}

// Helper to get a value from a nested object based on a path string
// Returns undefined if any part of the path doesn't exist
export function getNestedValue<T extends Record<string, any>>(
    obj: T,
    path: string
): SettingValue {
    const keys = path.split('.');
    let current: any = obj;
    for (const key of keys) {
        if (FORBIDDEN_KEYS.has(key)) return undefined;
        if (current && typeof current === 'object' && key in current) {
            current = current[key]; // nosemgrep: javascript.lang.security.audit.prototype-pollution.prototype-pollution-loop.prototype-pollution-loop
        } else {
            return undefined;
        }
    }
    return current as SettingValue;
}

// Sorts categories in a logical order for the UI tabs
// Prioritizes important categories first, then alphabetical for others
export function getCategoriesFromSettingsConfig(
    settingsConfig: Record<string, any[]>
): string[] {
    const preferredOrder = ['General', 'Appearance', 'Application', 'Chat', 'Export', 'Tools', 'System'];
    const categories = Object.keys(settingsConfig);
    
    categories.sort((a, b) => {
        const aIndex = preferredOrder.indexOf(a);
        const bIndex = preferredOrder.indexOf(b);
        
        // If both are in preferred order, sort by their index
        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }
        
        // If only one is in preferred order, it comes first
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
        // If neither is in preferred order, sort alphabetically
        return a.localeCompare(b);
    });
    
    return categories;
}

// Helper to determine if a setting label should show token count
export function needsTokenCount(label: string | undefined): boolean {
    const condition = 
        (typeof label === 'string' 
            && (label.toLowerCase().includes("prompt") 
            || label.toLowerCase().includes("rule")
        ));
    return condition;
}

// Word-based token estimation with symbol consideration
export function estimateTokens(text: string): number {
    if (!text.trim()) return 0;
    const SYMBOL_REGEX = /[.,!?;:()[\]{}"'`@#$%^&*+=<>/\\|~_-]/g;

    const wordCount = text.trim().split(/\s+/).length;
    const symbolCount = (text.match(SYMBOL_REGEX) || []).length;
    const newlineCount = (text.match(/\n/g) || []).length;
    
    // Rough estimate with buffer for subword tokenization
    return Math.ceil((wordCount + symbolCount + newlineCount) * 1.1);
}

// Helper function to validate and normalize numeric values for DualInput
// Centralizes validation logic for consistent numeric input handling
export function validateNumericValue(
    input: number,
    min?: number,
    max?: number,
    step: number = 1
): number {
    const stepString = step.toString();
    const decimalPlaces = (stepString.split('.')[1] || '').length;

    // Apply min/max bounds
    let boundedValue = input;
    if (min !== undefined && boundedValue < min) boundedValue = min;
    if (max !== undefined && boundedValue > max) boundedValue = max;

    // Snap to step increments (relative to min when available for grid alignment)
    const anchor = min ?? 0;
    const steppedValue = anchor + Math.round((boundedValue - anchor) / step) * step;

    // Apply precision and re-clamp (step snap can overshoot bounds)
    const precisedValue = parseFloat(steppedValue.toFixed(decimalPlaces));
    let result = precisedValue;
    if (min !== undefined && result < min) result = min;
    if (max !== undefined && result > max) result = max;
    return result;
}

